import { ReactNode, useMemo } from 'react';
import {
  ExporterCostCategory,
  normalizeReceiverCostConcept,
  UNCLASSIFIED_EXPORTER_COST_LABEL,
} from 'common/src/api/exporter-cost-categories';

interface CostLineInput {
  originalName: string;
  amount: number;
  currency: string;
}

interface ExporterCostSummaryProps {
  /** Raw cost lines on screen; ignored when `categoryTotalsLocal` is given. */
  lines: CostLineInput[];
  categories: ExporterCostCategory[];
  /** Normalized receiver cost name -> exporter category id. */
  mappings: Record<string, number>;
  /** Saved per-category totals (local currency), e.g. for shared costs. */
  categoryTotalsLocal?: Array<{
    categoryId: number | null;
    amountLocal: number;
  }>;
  localCurrency: string;
  exchangeRate: number;
  revenueLocal: number;
  /** FruitMarkets cost total in local currency (freight converted). */
  fmCostsLocal: number;
  /** The dialog's own (editable) exchange-rate field. */
  exchangeRateField: ReactNode;
  /** Opens the cost-line editor on a category's lines (null = Sin clasificar). */
  onCategoryPress?: (categoryId: number | null) => void;
}

const normalizeCurrency = (value: string | null | undefined) => {
  const normalized = String(value || '')
    .trim()
    .toUpperCase();
  return normalized === 'CNY' ? 'RMB' : normalized;
};

const formatUsd = (value: number) =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/**
 * The settlement's costs grouped by the exporter's own categories, laid out
 * like the FruitMarkets cost box (read-only). Anything the cost lines don't
 * explain, or that has no category, is shown as "Sin clasificar", so the
 * total always equals the FruitMarkets total. Render inside the same
 * `space-y-4` box as the FruitMarkets view.
 */
const ExporterCostSummary = ({
  lines,
  categories,
  mappings,
  categoryTotalsLocal,
  localCurrency,
  exchangeRate,
  revenueLocal,
  fmCostsLocal,
  exchangeRateField,
  onCategoryPress,
}: ExporterCostSummaryProps) => {
  const fx = exchangeRate > 0 ? exchangeRate : 1;
  const currency = normalizeCurrency(localCurrency) || 'USD';
  const showUsd = currency !== 'USD';

  const rows = useMemo(() => {
    const totals = new Map<number | null, number>();
    const add = (categoryId: number | null, amount: number) =>
      totals.set(categoryId, (totals.get(categoryId) || 0) + amount);

    if (categoryTotalsLocal) {
      categoryTotalsLocal.forEach((total) =>
        add(total.categoryId, total.amountLocal)
      );
    } else {
      lines.forEach((line) => {
        const amountLocal =
          normalizeCurrency(line.currency) === 'USD' && currency !== 'USD'
            ? line.amount * fx
            : line.amount;
        const categoryId =
          mappings[normalizeReceiverCostConcept(line.originalName)] ?? null;
        add(categoryId, amountLocal);
      });
      // Costs in the FruitMarkets totals that no cost line explains.
      const explained = Array.from(totals.values()).reduce(
        (sum, amount) => sum + amount,
        0
      );
      if (Math.abs(fmCostsLocal - explained) >= 0.01) {
        add(null, fmCostsLocal - explained);
      }
    }

    return [
      ...categories
        .filter((category) => totals.has(category.id))
        .map((category) => ({
          key: String(category.id),
          categoryId: category.id as number | null,
          name: category.name,
          amountLocal: totals.get(category.id) || 0,
        })),
      ...(totals.has(null)
        ? [
            {
              key: 'unclassified',
              categoryId: null,
              name: UNCLASSIFIED_EXPORTER_COST_LABEL,
              amountLocal: totals.get(null) || 0,
            },
          ]
        : []),
    ].filter((row) => Math.abs(row.amountLocal) >= 0.01);
  }, [
    categories,
    categoryTotalsLocal,
    currency,
    fmCostsLocal,
    fx,
    lines,
    mappings,
  ]);

  const totalLocal = rows.reduce((sum, row) => sum + row.amountLocal, 0);
  const subtotalLocal = revenueLocal - totalLocal;

  return (
    <>
      {/* Total Ingresos */}
      <div className='flex justify-between items-center py-2 border-b border-divider'>
        <span className='font-semibold text-green-600 dark:text-green-400'>
          Total Ingresos:
        </span>
        <span className='font-bold text-green-600 dark:text-green-400 flex items-baseline gap-2'>
          <span>
            {revenueLocal.toLocaleString()} {currency}
          </span>
          {showUsd && (
            <span className='text-xs text-default-400 font-normal'>
              (≈ {formatUsd(revenueLocal / fx)} USD)
            </span>
          )}
        </span>
      </div>

      {/* Exporter categories */}
      {rows.map((row) => (
        <div key={row.key} className='flex items-center gap-2'>
          <label
            className={`text-sm font-medium min-w-20 flex-shrink-0 ${
              row.key === 'unclassified'
                ? 'text-warning-600'
                : 'text-red-600 dark:text-red-400'
            }`}
          >
            {row.name}:
          </label>
          <div
            className={`bg-default-200 p-2 rounded flex items-center gap-1 ${
              onCategoryPress ? 'cursor-pointer hover:bg-default-300' : ''
            }`}
            onClick={() => onCategoryPress?.(row.categoryId)}
            title={
              onCategoryPress
                ? 'Ver y editar las líneas originales de esta categoría'
                : undefined
            }
          >
            <span className='font-medium'>
              {row.amountLocal.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })}
            </span>
            <span className='text-xs'>{currency}</span>
          </div>
          <span className='text-xs text-default-400 ml-2'>
            ({((row.amountLocal / (revenueLocal || 1)) * 100).toFixed(2)}%)
          </span>
          {showUsd && (
            <span className='text-xs text-default-400 ml-2'>
              ≈ {formatUsd(row.amountLocal / fx)} USD
            </span>
          )}
        </div>
      ))}

      {/* Total Costos */}
      <div className='flex justify-between items-center py-2 border-t border-divider'>
        <span className='font-semibold text-red-600 dark:text-red-400'>
          Total Costos:
        </span>
        <span className='font-bold text-red-600 dark:text-red-400'>
          {totalLocal.toLocaleString(undefined, { maximumFractionDigits: 2 })}{' '}
          {currency}
        </span>
      </div>

      {/* Subtotal */}
      <div className='flex justify-between items-center py-2 border-t border-divider'>
        <span className='font-semibold'>Subtotal Moneda Local:</span>
        <span className='font-bold'>
          {subtotalLocal.toLocaleString(undefined, {
            maximumFractionDigits: 2,
          })}{' '}
          {currency}
        </span>
      </div>

      {/* Tipo de Cambio */}
      <div className='flex items-center gap-2'>
        <label className='text-sm font-medium w-20 flex-shrink-0'>
          Tipo de Cambio:
        </label>
        {exchangeRateField}
      </div>

      {/* FOB Final */}
      <div className='flex justify-between items-center py-2 border-t-2 border-default-400 bg-blue-50 dark:bg-blue-950 px-2 rounded'>
        <span className='font-bold text-blue-700 dark:text-blue-300'>
          FOB Total:
        </span>
        <span className='font-bold text-blue-700 dark:text-blue-300 text-lg flex items-baseline gap-2'>
          <span>{formatUsd(subtotalLocal / fx)} USD</span>
          {showUsd && (
            <span className='text-sm text-default-500 font-normal'>
              (≈{' '}
              {subtotalLocal.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })}{' '}
              {currency})
            </span>
          )}
        </span>
      </div>

      <p className='text-xs text-default-400'>
        Haz clic en un monto para ver y editar sus líneas originales; ahí
        también puedes cambiar su categoría exportadora.
      </p>
    </>
  );
};

export default ExporterCostSummary;
